import { CloseOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button } from 'sutra/components/Button'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { Popconfirm } from 'sutra/components/Popconfirm/Popconfirm'
import { Toggle } from 'sutra/components/Toggle/Toggle'

import {
  HeaderControls,
  HeaderTitleGroup,
  PanelHeader,
  PanelTitle,
  UnreadCountLabel,
  UnreadToggleLabel,
} from 'features/notifications/components/notifications-panel-view/styled'
import type { NotificationsPanelViewProps } from 'features/notifications/types'

type NotificationsPanelHeaderProps = Pick<
  NotificationsPanelViewProps,
  | 'unreadCount'
  | 'onlyUnread'
  | 'onOnlyUnreadChange'
  | 'onToggleSelectAll'
  | 'onClearSelection'
  | 'onMarkSelectedRead'
  | 'onMarkSelectedUnread'
  | 'onDeleteSelected'
> & {
  hasRows: boolean
  allSelected: boolean
  selectedCount: number
  selectionHasUnread: boolean
}

/**
 * Extracted from NotificationsPanelView so that file stays under bully's
 * component-file-too-large threshold. Pure render — same purity contract
 * as the parent view (no service/store/query hooks).
 */
export const NotificationsPanelHeader = ({
  hasRows,
  allSelected,
  selectedCount,
  onToggleSelectAll,
  selectionHasUnread,
  onMarkSelectedRead,
  onMarkSelectedUnread,
  onDeleteSelected,
  onClearSelection,
  unreadCount,
  onlyUnread,
  onOnlyUnreadChange,
}: NotificationsPanelHeaderProps): JSX.Element => (
  <PanelHeader>
    <HeaderTitleGroup>
      {hasRows && (
        <Checkbox
          checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected}
          onChange={onToggleSelectAll}
          aria-label="Select all notifications"
          data-testid="notifications-select-all"
        />
      )}
      <PanelTitle>Notifications</PanelTitle>
    </HeaderTitleGroup>
    {selectedCount > 0 ? (
      // Contextual bulk toolbar — replaces the unread controls while a
      // selection is active so the header carries one mode at a time.
      <HeaderControls data-testid="notifications-selection-toolbar">
        <UnreadCountLabel>{selectedCount} selected</UnreadCountLabel>
        <Button
          variant="secondary"
          size="xs"
          onClick={
            selectionHasUnread ? onMarkSelectedRead : onMarkSelectedUnread
          }
          data-testid="notifications-selected-toggle"
        >
          {selectionHasUnread ? 'Mark read' : 'Mark unread'}
        </Button>
        <Popconfirm
          title={`Delete ${selectedCount} notification${selectedCount === 1 ? '' : 's'}?`}
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={onDeleteSelected}
        >
          <Button
            size="xs"
            variant="text"
            danger
            icon={<DeleteOutlined />}
            aria-label="Delete selected notifications"
            data-testid="notifications-selected-delete"
          />
        </Popconfirm>
        <Button
          size="xs"
          variant="text"
          icon={<CloseOutlined />}
          aria-label="Clear selection"
          onClick={onClearSelection}
          data-testid="notifications-selection-clear"
        />
      </HeaderControls>
    ) : (
      <HeaderControls>
        {unreadCount > 0 && (
          <UnreadCountLabel data-testid="notifications-unread-label">
            {unreadCount} unread
          </UnreadCountLabel>
        )}
        <UnreadToggleLabel>
          <Toggle
            size="sm"
            checked={onlyUnread}
            onChange={onOnlyUnreadChange}
            aria-label="Only show unread"
            data-testid="notifications-only-unread"
          />
          Only unread
        </UnreadToggleLabel>
      </HeaderControls>
    )}
  </PanelHeader>
)
